import {
  getRegionId,
  getSecurityId,
  getUserSelectedLanguage,
} from 'reducers/users';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import API from 'services/api';
import { setLoading, showSnackbar } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { getTimezone } from 'reducers/utils';
import {
  tripActions,
  tripPageActions,
  tripFilterActions,
} from 'containers/Pages/Tasks/Trips';

import { t } from 'i18next';
import { translatedKeyValueText } from 'utils/methods/helpers';

export function* fetchTripListSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { filters, from } = payload;
    const timezone = yield select(getTimezone);
    const securityCompanyId = yield select(getSecurityId);
    const regionId = yield select(getRegionId);
    const { data } = yield call(API.fetchTripList, {
      securityCompanyId,
      regionId,
      from,
      timezone,
      ...filters,
    });
    if (data.data) {
      const totalEntries = get(data, 'meta.total');
      yield put(tripActions.setGridList({ list: data.data, totalEntries }));
    }
    yield put(tripFilterActions.setFilterKey(false));
    yield put(tripActions.setForceRefresh(false));
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}

export function* fetchTripDetailSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    const { data } = yield call(API.fetchTripDetail, id);
    if (data.data) {
      yield put(tripActions.setGridRowDetails({ details: data.data }));
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
export function* cancelTripSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    const { data } = yield call(API.abortTrip, id);
    if (data) {
      yield put(
        showSnackbar({
          typeOfSnackbar: 'success',
          message: `${t('Trip cancelled')}`,
        }),
      );
      yield put(tripActions.setGridRowDetails({ details: data.data }));
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
export function* assignDriverSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id, driver } = payload;
    const { data } = yield call(API.assignDriver, { id, driver });
    if (data.data) {
      yield put(tripActions.setGridRowDetails({ details: data.data }));
      const { firstName } = driver.general;
      const { lastName } = driver.general;
      yield put(
        showSnackbar({
          typeOfSnackbar: 'success',
          message: translatedKeyValueText('label_trip_assigned', {
            message: 'Trip assigned to ', // this will be default text incase the translation is not provided
            value: `${firstName} ${lastName}`,
          }),
        }),
      );
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}

export function* printTripListSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { filters } = payload;
    const timezone = yield select(getTimezone);
    const regionId = yield select(getRegionId);
    const securityCompanyId = yield select(getSecurityId);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchTripListPdf, {
      regionId,
      securityCompanyId,
      timezone,
      language: language.toUpperCase(),
      ...filters,
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
export function* printTripDetailsSaga({ payload }) {
  try {
    const { id } = payload;
    yield put(setLoading(true));
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchTripDetailsPdf, {
      id,
      timezone,
      language: language.toUpperCase(),
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
export function* tripSagas() {
  yield takeLatest(tripActions.fetchGridList, fetchTripListSaga);
  yield takeLatest(tripActions.fetchGridRowDetails, fetchTripDetailSaga);
  yield takeLatest(tripPageActions.assignDriver, assignDriverSaga);
  yield takeLatest(tripPageActions.printTripList, printTripListSaga);
  yield takeLatest(tripPageActions.printTripDetails, printTripDetailsSaga);
  yield takeLatest(tripPageActions.cancelTrip, cancelTripSaga);
}
